import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import UrlForm from '../components/forms/UrlForm';
import DashboardLayout from '../components/layout/DashboardLayout';
import LinkedInAdsDisplay from '../components/LinkedInAdsDisplay';
import GoogleAdsTableDisplay from '../components/GoogleAdsTableDisplay';
import GoogleAdAnalysisSummary from '../components/GoogleAdAnalysisSummary';
import AdAnalysisSummary from '../components/AdAnalysisSummary';
import InsightsCTA from '../components/InsightsCTA';
import { useCompetitorData } from '../hooks/useCompetitorData';
import { supabase, fetchLinkedInAds, checkCompanyExists, fetchGoogleAds, getCompanyName } from '../lib/supabase';
import { LinkedInAd, GoogleAd } from '../types/dataTypes';

const Dashboard: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [currentCompanyId, setCurrentCompanyId] = useState(companyId || '431');
  const [linkedInAds, setLinkedInAds] = useState<LinkedInAd[]>([]);
  const [googleAds, setGoogleAds] = useState<GoogleAd[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  
  const { 
    competitorData,
    analyzeCompetitors,
    selectedDateRange,
    setSelectedDateRange
  } = useCompetitorData();

  useEffect(() => {
    const validateAndSetCompanyId = async () => {
      if (companyId) {
        const exists = await checkCompanyExists(companyId);
        if (!exists) {
          console.log('Company ID not found in database:', companyId);
          navigate('/company/431'); // Redirect to default if ID not found
          return;
        }
        setCurrentCompanyId(companyId);
        
        // Fetch company name
        const name = await getCompanyName(companyId);
        setCompanyName(name);
      }
    };

    validateAndSetCompanyId();
  }, [companyId, navigate]);

  useEffect(() => {
    const loadAdsData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch both LinkedIn and Google ads in parallel
        const [linkedInResults, googleResults] = await Promise.all([
          fetchLinkedInAds(currentCompanyId),
          fetchGoogleAds(currentCompanyId)
        ]);
        
        setLinkedInAds(linkedInResults);
        setGoogleAds(googleResults);
      } catch (err) {
        console.error('Error loading ads data:', err);
        setError('Failed to load ads data');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentCompanyId) {
      loadAdsData();
    }
  }, [currentCompanyId]);

  const handleCompanyIdChange = async (newCompanyId: string) => {
    const exists = await checkCompanyExists(newCompanyId);
    if (exists) {
      navigate(`/company/${newCompanyId}`);
    } else {
      setError(`Company ID ${newCompanyId} not found in database`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Navbar companyId={currentCompanyId} onCompanyIdChange={handleCompanyIdChange} />
      <main className="container mx-auto px-4 py-8 flex-grow">
        {companyName && (
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            {companyName}'s Custom Dashboard
          </h1>
        )}
        
        <UrlForm 
          onAnalyze={analyzeCompetitors} 
          isLoading={isLoading} 
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
        />

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-8">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              <LinkedInAdsDisplay ads={linkedInAds} />
              {linkedInAds.length > 0 && <AdAnalysisSummary ads={linkedInAds} />}
            </>
          )}
          
          <GoogleAdsTableDisplay companyId={currentCompanyId} />
          {googleAds.length > 0 && (
            <GoogleAdAnalysisSummary ads={googleAds} />
          )}

          <InsightsCTA />
        </div>

        {competitorData && !isLoading && (
          <div className="mt-8">
            <DashboardLayout data={competitorData} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;